/**
 * wavEncoder.js — Browser-side PCM → 16-bit WAV encoder.
 *
 * We can't rely on MediaRecorder to give us a decodable WAV (it usually
 * hands us webm/opus, which the backend can't parse without ffmpeg). So
 * we tap the microphone via Web Audio API, buffer Float32 PCM chunks,
 * then convert to a 16-bit PCM WAV blob at stop time.
 *
 * Usage:
 *   const rec = await createWavRecorder({ sampleRate: 16000 });
 *   rec.start();
 *   // ...
 *   const blob = await rec.stop(); // Blob<audio/wav>
 */

const TARGET_SAMPLE_RATE = 16000;

export async function createWavRecorder({ sampleRate = TARGET_SAMPLE_RATE } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioCtor();
  const source = audioCtx.createMediaStreamSource(stream);

  // Web Audio's ScriptProcessorNode is deprecated but still the most
  // reliable cross-browser way to capture raw PCM without shipping an
  // AudioWorklet module in every deployment. 4096-frame buffer keeps
  // latency reasonable while limiting event overhead.
  const proc = audioCtx.createScriptProcessor(4096, 1, 1);
  const chunks = [];
  let capturing = false;

  proc.onaudioprocess = (e) => {
    if (!capturing) return;
    const input = e.inputBuffer.getChannelData(0);
    // Copy — the underlying buffer is reused between callbacks.
    chunks.push(new Float32Array(input));
  };

  source.connect(proc);
  proc.connect(audioCtx.destination);

  const cleanup = () => {
    try { proc.disconnect(); } catch (_) {}
    try { source.disconnect(); } catch (_) {}
    stream.getTracks().forEach((t) => t.stop());
    try { audioCtx.close(); } catch (_) {}
  };

  return {
    stream,
    audioCtx,
    start() { capturing = true; },
    stop() {
      capturing = false;
      const merged = flattenChunks(chunks);
      cleanup();
      const downsampled = downsample(merged, audioCtx.sampleRate, sampleRate);
      const wavBuffer = encodeWavPcm16(downsampled, sampleRate);
      return new Blob([wavBuffer], { type: "audio/wav" });
    },
    cancel() {
      capturing = false;
      cleanup();
    },
  };
}

function flattenChunks(chunks) {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Float32Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

function downsample(samples, fromRate, toRate) {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const outLen = Math.floor(samples.length / ratio);
  const out = new Float32Array(outLen);
  let idx = 0;
  let src = 0;
  while (idx < outLen) {
    const nextSrc = Math.floor((idx + 1) * ratio);
    let sum = 0;
    let count = 0;
    for (let i = src; i < nextSrc && i < samples.length; i++) {
      sum += samples[i];
      count++;
    }
    out[idx] = count ? sum / count : 0;
    idx++;
    src = nextSrc;
  }
  return out;
}

function encodeWavPcm16(samples, sampleRate) {
  const bytesPerSample = 2;
  const blockAlign = 1 * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);            // PCM
  view.setUint16(22, 1, true);            // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);           // bits per sample
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function writeString(view, offset, s) {
  for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
}
